import React, { useState, useEffect, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  IconButton,
  TextField,
  Tooltip,
  Button,
  InputAdornment,
} from "@mui/material";
import {
  ArrowBackIos,
  ArrowForwardIos,
  FileDownload,
  Print,
  Close,
} from "@mui/icons-material";
import { minWidth, styled } from "@mui/system";
import NoDataComponent from "@/components/NoData/NoDataComponent";
import { Search as SearchIcon } from "@mui/icons-material";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "rgb(234, 245, 238) !important",
    fontWeight: "bold",
    fontSize: "clamp(12px, 1.8vw, 14px)",
    height: "clamp(50px, 6vh, 60px)",
    fontFamily: "Lato",
    borderBottom: "2px solid #ddd",
  },
  "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
    backgroundColor: "rgb(234, 245, 238) !important", // Force override
    fontSize: "clamp(13px, 1.85vw, 15px)",
    height: "clamp(50px, 6vh, 60px)",
    fontFamily: "Lato",
    borderBottom: "2px solid #ddd",
  },
  "& .MuiDataGrid-cell": {
    fontSize: "clamp(10px, 1.6vw, 13px)",
    padding: "10px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontFamily: "Lato",
    alignContent: "center",
    outline: "none !important",
  },
  "& .MuiDataGrid-root": {
    border: "none",
  },
  "& .MuiDataGrid-virtualScroller": {
    overflowX: "auto !important",
  },
  "& .MuiDataGrid-columnHeader:first-of-type": {
    visibility: "hidden",
    pointerEvents: "none",
  },
  "& .MuiDataGrid-cell:first-of-type": {
    visibility: "hidden",
    pointerEvents: "none",
  },
}));

const CustomPagination = ({
  count,
  page,
  setPage,
  size,
  setPageSize,
  pageCount,
}) => {
  // Use pageCount from parent instead of calculating totalPages locally
  const totalPages = pageCount || Math.max(Math.ceil(count / size), 1);
  const startRecord = count === 0 ? 0 : page * size + 1;
  const endRecord = Math.min((page + 1) * size, count);

  // State to track which ellipsis sections are expanded
  const [expandedSections, setExpandedSections] = useState({
    left: false,
    right: false,
  });

  // Generate pagination items with "1 2 ... totalPages" format
  const generatePaginationItems = () => {
    const items = [];
    const currentPage = page + 1; // Convert to 1-based indexing for display

    if (totalPages <= 7) {
      // If total pages <= 7, show all pages
      for (let i = 1; i <= totalPages; i++) {
        items.push(i);
      }
    } else {
      // Always show page 1
      items.push(1);

      if (currentPage <= 4) {
        // Show 1, 2, 3, 4, ..., totalPages
        for (let i = 2; i <= Math.min(4, currentPage + 1); i++) {
          items.push(i);
        }

        if (expandedSections.left) {
          // Show only 2 pages after the current visible range
          const startExpand = Math.min(5, currentPage + 2);
          const endExpand = Math.min(startExpand + 1, totalPages - 1); // Show only 2 pages
          for (let i = startExpand; i <= endExpand; i++) {
            items.push(i);
          }
          // Add ellipsis if there are still more pages
          if (endExpand < totalPages - 1) {
            items.push({
              type: "ellipsis",
              position: "left",
              range: [endExpand + 1, totalPages - 1],
            });
          }
        } else {
          items.push({
            type: "ellipsis",
            position: "left",
            range: [Math.min(5, currentPage + 2), totalPages - 1],
          });
        }
        items.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show 1, ..., totalPages-3, totalPages-2, totalPages-1, totalPages
        if (expandedSections.left) {
          // Show only 2 pages before the current visible range
          const endExpand = Math.max(totalPages - 4, currentPage - 2);
          const startExpand = Math.max(2, endExpand - 1); // Show only 2 pages
          // Add ellipsis if there are still more pages before
          if (startExpand > 2) {
            items.push({
              type: "ellipsis",
              position: "left",
              range: [2, startExpand - 1],
            });
          }
          for (let i = startExpand; i <= endExpand; i++) {
            items.push(i);
          }
        } else {
          items.push({
            type: "ellipsis",
            position: "left",
            range: [2, Math.max(totalPages - 4, currentPage - 2)],
          });
        }

        for (
          let i = Math.max(totalPages - 3, currentPage - 1);
          i <= totalPages;
          i++
        ) {
          items.push(i);
        }
      } else {
        // Show 1, ..., currentPage-1, currentPage, currentPage+1, ..., totalPages
        if (expandedSections.left) {
          // Show only 2 pages before current page
          const endExpand = currentPage - 2;
          const startExpand = Math.max(2, endExpand - 1); // Show only 2 pages
          // Add ellipsis if there are still more pages before
          if (startExpand > 2) {
            items.push({
              type: "ellipsis",
              position: "left",
              range: [2, startExpand - 1],
            });
          }
          for (let i = startExpand; i <= endExpand; i++) {
            items.push(i);
          }
        } else {
          items.push({
            type: "ellipsis",
            position: "left",
            range: [2, currentPage - 2],
          });
        }

        items.push(currentPage - 1);
        items.push(currentPage);
        items.push(currentPage + 1);

        if (expandedSections.right) {
          // Show only 2 pages after current page
          const startExpand = currentPage + 2;
          const endExpand = Math.min(startExpand + 1, totalPages - 1); // Show only 2 pages
          for (let i = startExpand; i <= endExpand; i++) {
            items.push(i);
          }
          // Add ellipsis if there are still more pages after
          if (endExpand < totalPages - 1) {
            items.push({
              type: "ellipsis",
              position: "right",
              range: [endExpand + 1, totalPages - 1],
            });
          }
        } else {
          items.push({
            type: "ellipsis",
            position: "right",
            range: [currentPage + 2, totalPages - 1],
          });
        }
        items.push(totalPages);
      }
    }

    return items;
  };

  const handleEllipsisClick = (position) => {
    setExpandedSections((prev) => ({
      ...prev,
      [position]: !prev[position],
    }));
  };

  // Reset expanded sections when page changes
  useEffect(() => {
    setExpandedSections({ left: false, right: false });
  }, [page]);

  const paginationItems = generatePaginationItems();

  return (
    <Box>
      {/* Top row: "Displaying..." aligned left */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start", // Align to start (left)
          alignItems: "center",
          width: "100%",
          px: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            fontSize: "clamp(10px, 1.6vw, 13px)",
            fontFamily: "Lato",
          }}
        >
          Displaying {startRecord} to {endRecord} of {count} records
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center", // Center the pagination
          alignItems: "center",
          mt: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            size="small"
            onClick={() => setPage(page - 1)}
            disabled={page === 0}
          >
            <ArrowBackIos fontSize="small" />
          </IconButton>
          {paginationItems.map((item, index) => {
            const isEllipsis =
              typeof item === "object" && item.type === "ellipsis";
            const isNumber = typeof item === "number";
            const displayValue = isEllipsis ? "..." : item;

            return (
              <Box
                key={`${isEllipsis ? `ellipsis-${item.position}` : item
                  }-${index}`}
                onClick={() => {
                  if (isNumber) {
                    setPage(item - 1); // Convert back to 0-based indexing
                  } else if (isEllipsis) {
                    handleEllipsisClick(item.position);
                  }
                }}
                sx={{
                  width: isEllipsis ? "auto" : 30,
                  height: 30,
                  borderRadius: isEllipsis ? 0 : "50%",
                  backgroundColor: isEllipsis
                    ? "transparent"
                    : page === item - 1
                      ? "#2D6443"
                      : "lightgray",
                  color: isEllipsis ? "#666" : "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "clamp(12px, 1.8vw, 14px)",
                  cursor: isEllipsis
                    ? "pointer"
                    : isNumber
                      ? "pointer"
                      : "default",
                  px: isEllipsis ? 1 : 0,
                  "&:hover": isEllipsis
                    ? {
                      backgroundColor: "#f0f0f0",
                      borderRadius: "4px",
                    }
                    : {},
                }}
              >
                {displayValue}
              </Box>
            );
          })}
          <IconButton
            size="small"
            onClick={() => setPage(page + 1)}
            disabled={page + 1 >= pageCount}
          >
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </Box>
      </Box>

    </Box>
  );
};
// (Keep all your imports and component definitions above this as-is)

const CommonDataTable = ({
  rows,
  columns,
  checkbox,
  onSelectedRowsChange,
  disableRowSelectionOnClick,
  onRowSelectionModelChange,
  selectedRows,
  isRowSelectable,
  checkboxConditionFn = () => true,
  exportOptions = {
    csv: false,
    excel: false,
    pdf: false,
    print: false,
  },
  pageCount,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  currentPage,
  currentPageSize,
  search = true,
  onSearchChange,
  searchValue = "",
  loading,
}) => {
  const [page, setPage] = useState(currentPage || 0);
  const [size, setPageSize] = useState(currentPageSize || 10);
  const [selectionModel, setSelectionModel] = useState([]);

  useEffect(() => {
    setPage(0);
    if (onPageChange) {
      onPageChange(0);
    }
  }, [onPageChange]);

  useEffect(() => {
    if (selectedRows) {
      const newSelection = selectedRows.map((row) => row.id);
      setSelectionModel(newSelection);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (currentPage !== undefined && currentPage !== page) {
      setPage(currentPage);
    }
  }, [currentPage]);

  useEffect(() => {
    if (currentPageSize !== undefined && currentPageSize !== size) {
      setPageSize(currentPageSize);
    }
  }, [currentPageSize]);

  const rowsWithId = useMemo(
    () => rows?.map((row, index) => ({ id: row.id ?? `${index}`, ...row })),
    [rows]
  );

  const updatedColumns = [
    {
      field: "index",
      headerName: "S.No",
      minWidth: 100,
      renderCell: (params) =>
        page * size + (params.api.getSortedRowIds().indexOf(params.id) + 1),
    },
    ...columns.map((col) => ({
      ...col,
      minWidth: col.minWidth || 180,
      renderCell: (params) => {
        const value = params.value;
        if (col.renderCell) return col.renderCell(params);
        return value !== null && value !== undefined && value !== ""
          ? value
          : "-";
      },
    })),
  ];

  const renderExportButtons = () => (
    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
      {exportOptions.csv && (
        <Tooltip title="Export as CSV">
          <Button
            sx={{
              height: "30px",
              borderRadius: "6px",
              background: "#2B5F40",
              color: "rgb(255, 255, 255)",
            }}
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => handleExport("csv")}
            size="small"
          >
            CSV
          </Button>
        </Tooltip>
      )}
      {exportOptions.excel && (
        <Tooltip title="Export as Excel">
          <Button
            sx={{
              height: "30px",
              borderRadius: "6px",
              background: "#2B5F40",
              color: "rgb(255, 255, 255)",
            }}
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => handleExport("excel")}
            size="small"
          >
            Excel
          </Button>
        </Tooltip>
      )}
      {exportOptions.pdf && (
        <Tooltip title="Export as PDF">
          <Button
            sx={{
              height: "30px",
              borderRadius: "6px",
              background: "#2B5F40",
              color: "rgb(255, 255, 255)",
            }}
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => handleExport("pdf")}
            size="small"
          >
            PDF
          </Button>
        </Tooltip>
      )}
      {exportOptions.print && (
        <Tooltip title="Print">
          <Button
            sx={{
              height: "30px",
              borderRadius: "6px",
              background: "#2B5F40",
              color: "rgb(255, 255, 255)",
            }}
            variant="outlined"
            startIcon={<Print />}
            onClick={() => handleExport("print")}
            size="small"
          >
            Print
          </Button>
        </Tooltip>
      )}
    </Box>
  );

  const handleSelectionChange = (newSelection) => {
    const validSelection = newSelection.filter((id) => {
      const row = rowsWithId.find((r) => r.id === id);
      return checkboxConditionFn ? checkboxConditionFn(row) : true;
    });
    setSelectionModel(validSelection);
    const selectedRows = rowsWithId.filter((row) =>
      validSelection.includes(row.id)
    );
    onSelectedRowsChange?.(selectedRows);
  };

  useEffect(() => {
    setSelectionModel([]);
    onSelectedRowsChange?.([]);
  }, [page, size]);

  const handleExport = (type) => {
    const dataToExport = rowsWithId.map((row) => {
      const exportedRow = {};
      updatedColumns.forEach((col) => {
        if (col.field !== "index" && col.field !== "__check__") {
          exportedRow[col.headerName || col.field] = row[col.field];
        }
      });
      return exportedRow;
    });

    switch (type) {
      case "csv":
        exportToCSV(dataToExport);
        break;
      case "excel":
        exportToExcel(dataToExport);
        break;
      case "pdf":
        exportToPDF(dataToExport);
        break;
      case "print":
        window.print();
        break;
    }
  };

  const exportToCSV = (data) => {
    const csvContent = [
      Object.keys(data[0]).join(","),
      ...data.map((row) => Object.values(row).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = (data) => {
    import("xlsx").then((XLSX) => {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, "export.xlsx");
    });
  };

  const exportToPDF = (data) => {
    import("jspdf").then(({ default: jsPDF }) => {
      import("jspdf-autotable").then((autoTable) => {
        const doc = new jsPDF();
        autoTable.default(doc, {
          head: [Object.keys(data[0])],
          body: data.map((row) => Object.values(row)),
          styles: {
            cellPadding: 3,
            fontSize: 8,
            valign: "middle",
          },
          margin: { top: 20 },
        });
        doc.save("export.pdf");
      });
    });
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Top Toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          mt: 1,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "clamp(10px, 1.6vw, 13px)",
              fontFamily: "Lato",
            }}
          >
            Show
          </Typography>
          <Select
            value={size}
            onChange={(e) => {
              const newSize =
                e.target.value === "all" ? totalRecords : e.target.value;
              setPageSize(newSize);
              setPage(0);
              if (onPageSizeChange) onPageSizeChange(newSize);
            }}
            size="small"
            sx={{ height: 30, minWidth: 80 }}
          >
            {[5, 10, 20, 50, 100, { value: "all", label: "All" }].map((size) => {
              const value = typeof size === "object" ? size.value : size;
              const label = typeof size === "object" ? size.label : size;
              return (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              );
            })}
            {/* <MenuItem value="all">All</MenuItem> */}
          </Select>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "clamp(10px, 1.6vw, 13px)",
              fontFamily: "Lato",
            }}
          >
            entries
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {renderExportButtons()}
          {search && (
            <TextField
              value={searchValue}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ marginRight: "3px" }}>
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => onSearchChange?.("")}
                      sx={{
                        padding: "4px",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                      aria-label="clear search"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={(theme) => ({
                width: "100%",
                maxWidth: "250px",
                borderRadius: "32px",
                color: "#FAFAFA",
                backgroundColor: "#FAFAFA",
                border: `1px solid ${theme.palette.BudgieThemeColor.Neutrals[300]}`,
                "& .MuiOutlinedInput-root": {
                  padding: "0px",
                  height: "35px",
                  borderRadius: "32px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor:
                      theme.palette.BudgieThemeColor.Neutrals[100],
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.BudgieThemeColor.Neutrals[100],
                },
              })}
              onChange={(e) => {
                const value = e.target.value;
                onSearchChange?.(value);
              }}
            />
          )}
        </Box>
      </Box>

      {/* DataGrid */}
      <StyledDataGrid
        rows={rowsWithId || []}
        columns={updatedColumns}
        hideFooter
        checkboxSelection={checkbox}
        isRowSelectable={isRowSelectable}
        disableSelectionOnClick
        disableColumnMenu
        getRowHeight={() => "auto"}
        autoHeight
        sx={{
          minWidth: "100%",
          "& .hide-checkbox .MuiDataGrid-cellCheckbox": {
            visibility: "hidden",
            pointerEvents: "none",
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: "bold",
            outline: "none !important",
          },
        }}
        onRowSelectionModelChange={handleSelectionChange}
        rowSelectionModel={selectionModel}
        onCellClick={(params, event) => {
          const isCheckboxCell = event.target.closest(
            '.MuiDataGrid-cellCheckbox input[type="checkbox"]'
          );
          if (!isCheckboxCell) {
            event.stopPropagation();
          }
        }}
        disableRowSelectionOnClick={disableRowSelectionOnClick ?? false}
        getRowClassName={(params) => {
          const isCheckboxVisible = checkboxConditionFn
            ? checkboxConditionFn(params.row)
            : true;
          return isCheckboxVisible ? "" : "hide-checkbox";
        }}
        loading={loading}
      />

      <CustomPagination
        count={totalRecords}
        page={page}
        setPage={(newPage) => {
          setPage(newPage);
          if (onPageChange) onPageChange(newPage);
        }}
        size={size}
        pageCount={pageCount}
        setPageSize={(newPageSize) => {
          setPageSize(newPageSize);
          if (onPageSizeChange) onPageSizeChange(newPageSize);
        }}
      />
    </Box>
  );
};

export default CommonDataTable;

